import { useState, useEffect, useCallback } from "react";
import { cleanRut, validateRut, formatRut } from "../shared/validations";
import * as clienteService from "../../../services/ClienteService";

const useClienteForm = (clienteData, modoEdicion, baseUrl, toast) => {
  const [nuevaEmpresa, setNuevaEmpresa] = useState("");
  const [nuevoTipo, setNuevoTipo] = useState("");
  const [nuevoRut, setNuevoRut] = useState("");
  const [nuevaRazon, setNuevaRazon] = useState("");
  const [nuevoAlias, setNuevoAlias] = useState("");
  const [nuevoFanta, setNuevoFanta] = useState("");
  const [giro, setGiro] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [rutExiste, setRutExiste] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [camposTocados, setCamposTocados] = useState({
    empresa: false,
    rut: false,
    razonSocial: false,
    giro: false,
    alias: false,
    tipoCliente: false,
    nombreFantasia: false,
  });

  const [camposValidos, setCamposValidos] = useState({
    empresa: false,
    rut: false,
    razonSocial: false,
    giro: false,
    alias: false,
    tipoCliente: false,
    nombreFantasia: false,
  });

  const [formularioValido, setFormularioValido] = useState(false);

  const handleCampoTocado = (campo) => {
    setCamposTocados((prev) => ({ ...prev, [campo]: true }));
  };

  const mostrarError = (campo) => {
    if (campo === "rut") {
      return (camposTocados[campo] && !camposValidos[campo]) || rutExiste;
    }
    return camposTocados[campo] && !camposValidos[campo];
  };

  const validarCampos = useCallback(() => {
    const nuevosCamposValidos = {
      empresa: !!nuevaEmpresa,
      rut: isValid && !!nuevoRut,
      razonSocial: !!nuevaRazon,
      giro: !!giro,
      alias: !!nuevoAlias,
      tipoCliente: !!nuevoTipo,
      nombreFantasia: !!nuevoFanta,
    };
    setCamposValidos(nuevosCamposValidos);
    setFormularioValido(Object.values(nuevosCamposValidos).every((v) => v));
  }, [nuevaEmpresa, isValid, nuevoRut, nuevaRazon, giro, nuevoAlias, nuevoTipo, nuevoFanta]);

  useEffect(() => {
    validarCampos();
  }, [validarCampos]);

  const cargarEmpresas = async () => {
    try {
      const data = await clienteService.getAllEmpresas(baseUrl);
      setEmpresas(data);
    } catch (error) {
      console.error(error);
    }
  };

  const cargarTiposCliente = async () => {
    try {
      const data = await clienteService.getAllTiposCliente(baseUrl);
      setTipos(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    cargarEmpresas();
    cargarTiposCliente();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clienteData) {
      setNuevaEmpresa(clienteData.id_empresa || "");
      setNuevoTipo(clienteData.id_tipo_cliente || "");
      setNuevoRut(clienteData.rut || "");
      setIsValid(validateRut(cleanRut(clienteData.rut || "")));
      setNuevaRazon(clienteData.razon_social || "");
      setNuevoAlias(clienteData.alias || "");
      setNuevoFanta(clienteData.nombre_fantasia || "");
      setGiro(clienteData.giro || "");
    }
  }, [clienteData]);

  const handleRutChange = (event) => {
    const inputRut = event.target.value;
    setNuevoRut(inputRut);
    setIsValid(validateRut(cleanRut(inputRut)));
    setRutExiste(false);
  };

  const handleRutBlur = async () => {
    if (isValid) {
      setNuevoRut(formatRut(nuevoRut));
      const rutLimpio = nuevoRut.replace(/[.\s]/g, "");
      if (modoEdicion && cleanRut(clienteData?.rut || "") === cleanRut(rutLimpio)) {
        setRutExiste(false);
        return;
      }
      try {
        const data = await clienteService.getClienteByRut(baseUrl, rutLimpio);
        if (data === true) {
          setRutExiste(true);
          toast.current?.show({
            severity: "error",
            summary: "Rechazado",
            detail: "El RUT del cliente ya existe",
            life: 3000,
          });
        } else {
          setRutExiste(false);
        }
      } catch (error) {
        console.error("Error al verificar RUT:", error);
      }
    }
  };

  const checkFormClientes = () => {
    const missingFields = [];
    if (!nuevaEmpresa) missingFields.push("Empresa");
    if (!nuevoRut) {
      missingFields.push("Rut");
    } else if (!isValid) {
      missingFields.push("Rut (inválido)");
    }
    if (!nuevaRazon) missingFields.push("Razón Social / Nombre");
    if (!giro) missingFields.push("Giro");
    if (!nuevoTipo) missingFields.push("Tipo de Cliente");
    if (!nuevoAlias) missingFields.push("Alias");
    if (!nuevoFanta) missingFields.push("Nombre Fantasía");
    return missingFields;
  };

  return {
    nuevaEmpresa,
    setNuevaEmpresa,
    nuevoTipo,
    setNuevoTipo,
    nuevoRut,
    setNuevoRut,
    nuevaRazon,
    setNuevaRazon,
    nuevoAlias,
    setNuevoAlias,
    nuevoFanta,
    setNuevoFanta,
    giro,
    setGiro,
    isValid,
    rutExiste,
    empresas,
    tipos,
    loading,
    setLoading,
    camposTocados,
    camposValidos,
    formularioValido,
    handleCampoTocado,
    mostrarError,
    handleRutChange,
    handleRutBlur,
    checkFormClientes,
  };
};

export default useClienteForm;
